<script>
/**
 * <hidden-fields></hidden-fields>
 * @desc component to handle the hidden fields and
 * URL params that need to be passed with the application
 */
// import appSource from '@/mixins/appSource'
import marketingCloud from '@/mixins/marketing-cloud'
import cookies from '@/mixins/cookies'
import DataLayer from '@/data-layer'
import 'url-polyfill'

export default {
  // Name
  name: 'app-hidden-fields',

  // Mixins
  mixins: [cookies, marketingCloud],

  // Props
  props: {
    variation: String,
    appLanded: {
      type: String,
      required: false,
      default: 'app_landed'
    }
  },

  // Data
  data () {
    return {
      urlFields: {
        'AID': 'aid',
        'AID2': 'AID2',
        'SID': 'SID',
        'SID2': 'SID2',
        'gclid': 'gclid',
        'ReferrerId': 'referrer_id',
        'referrer_first_name': 'referrer_first_name',
        'referrer_last_name': 'referrer_last_name'
      },
      utmParamMap: {
        'SID': 'utm_source',
        'SID2': 'utm_medium',
        'AID': 'utm_campaign',
        'AID2': 'utm_content',
        'AD_Source': 'utm_term'
      },
      referrerURLs: [
        { string: 'facebook', url: 'www.facebook.com' },
        { string: 'fb', url: 'www.facebook.com' },
        { string: 'google', url: 'www.google.com' },
        { string: 'instagram', url: 'www.instagram.com' },
        { string: 'msn', url: 'www.msn.com' },
        { string: 'yahoo', url: 'www.yahoo.com' },
        { string: 'bing', url: 'www.bing.com' },
        { string: 'taboola', url: 'www.taboola.com' },
        { string: 'kijiji', url: 'www.kijiji.com' },
        { string: 'email', url: 'www.canadadrivesemail.com' },
        { string: 'twitter', url: 'www.twitter.com' },
        { string: 'autotrader', url: 'www.autotrader.com' }
      ],
      appName: 'canadadrives',
      countryCode: 'CA'
    }
  },

  // Computed
  computed: {
    app_source () {
      return `${this.appName}-${this.deviceType}-${this.variation}`
    },

    deviceType () {
      const devices = /Mobile|Tablet|Android|BlackBerry|iPhone|iPad|iPod|Opera Mini|IEMobile/i

      return navigator.userAgent.match(devices) ? 'm' : 'd'
    }
  },

  // Mounted
  created () {
    this.setAppSource()
    this.setURLFields()
    this.setValue('SID')
    this.setValue('AID', 'aid')
    this.setLeadSource()
    this.setAdSource()
    this.setReferrerURL()
    this.setUserAgent()
    this.setVehicleType()
    if ((process.env.VUE_APP_IOVATION_ENABLED || 'true') === 'true') this.setIovationFields()
    this.$_marketingCloud_setCookies()
    this.setWindowHiddenFields()
  },

  // Methods
  methods: {
    /**
    * Set the app_source and country_code fields in the Vuex store
    */
    setAppSource () {
      this.$store.updateApplication('app_source', this.app_source)
      this.$store.updateApplication('country_code', this.countryCode)

      DataLayer.pushEvent('app_source', this.app_source)
      DataLayer.pushEvent('event', this.appLanded)
    },

    /**
    * Set the app_source and country_code fields in the Vuex store
    */
    setUserAgent () {
      this.$store.updateApplication('user_agent', navigator.userAgent)
    },

    /**
    * Set the URL param fields
    * @desc loop through the urlFields and update the Vuex store
    */
    setURLFields () {
      for (let i in this.urlFields) {
        const item = this.urlFields[i]
        // Prioritize CD param value over UTM param value
        const urlParamValue = this.$route.query[i] || this.$route.query[this.utmParamMap[i]]
        if (urlParamValue) {
          this.$store.updateApplication(item, urlParamValue)
        } else if (this.$_cookies_getItem(item)) {
          this.$store.updateApplication(item, this.$_cookies_getItem(item))
        } else {
          this.$store.updateApplication(item, '')
        }
      }
    },

    /**
     * Set Vehicle Type
     * @desc If there is a 'vtype' url param, update type_of_car in the store
     */
    setVehicleType () {
      if (!this.$route.query.vehicle_type) return
      const vtype = this.$route.query.vehicle_type.toLowerCase()
      const vehicleTypes = ['truck', 'car', 'suv', 'van']
      if (vehicleTypes.indexOf(vtype) > -1) {
        this.$store.updateApplication('car_type', vtype)
      }
    },

    /**
    * Get the most recent SID
    * @returns {String}
    */
    getValue (value) {
      if (this.$route.query[value]) {
        return this.$route.query[value]
      } else if (this.$route.query[this.utmParamMap[value]]) {
        return this.$route.query[this.utmParamMap[value]]
      } else {
        if (this.$_cookies_getItem(value)) {
          return this.$_cookies_getItem(value).split(',').pop()
        } else {
          return undefined
        }
      }
    },

    /**
    * Set the SID field in the Vuex store
    */
    setValue (value, field) {
      let urlValue
      // Prioritize CD param value over UTM param value
      if (this.$route.query[value]) {
        urlValue = this.$route.query[value].replace(/[^\w-]/g, '')
      } else if (this.$route.query[this.utmParamMap[value]]) {
        urlValue = this.$route.query[this.utmParamMap[value]].replace(/[^\w-]/g, '')
      }
      if (!urlValue) return
      const cookieValue = this.setCookieValue(value, urlValue, this.$_cookies_getItem(value))
      this.$store.updateApplication(field || value, cookieValue)
    },

    /**
    * Set the SID cookie
    * @param newValue {string} SID from url parameters
    * @param oldValue {string} SID cookie string
    * @returns {string}
    */
    setCookieValue (name, newValue, oldValue) {
      let cookieValue = newValue
      if (oldValue) {
        let values = oldValue.split(',')
        if (!oldValue.endsWith(newValue) && newValue) {
          values.push(newValue)
        }
        cookieValue = values.toString()
      }
      this.$_cookies_setItem(name, cookieValue, new Date().setDate(new Date().getDate() + 30))
      return cookieValue
    },

    /**
    * Set the ad_source field in the Vuex store
    * Uses the most recent 'AID' value
    */
    setAdSource () {
      const adSource = this.getValue('AD_Source') || this.getValue('AID') || ''
      this.$store.updateApplication('ad_source', adSource)
    },

    /**
    * Set the lead_source field in the Vuex store
    */
    setLeadSource () {
      const SID = this.getValue('SID')
      const leadSource = SID
        ? SID.split('-')[0]
        : ''
      this.$store.updateApplication('lead_source', leadSource)
    },

    /**
    * Set the referrer_url field in the Vuex store
    */
    setReferrerURL () {
      const SID = this.getValue('SID')
      if (!SID) { return }
      for (let item of this.referrerURLs) {
        if (SID.indexOf(item.string) !== -1) {
          this.$store.updateApplication('referrer_url', item.url)
          return
        }
      }
    },

    setIovationFields () {
      setTimeout(() => {
        if (!window.ioGetBlackbox || !window.fpGetBlackbox) {
          return this.setIovationFields()
        }

        const ioBB = window.ioGetBlackbox()
        const fpBB = window.fpGetBlackbox()

        this.$store.updateApplication('ioBB', ioBB.blackbox)
        this.$store.updateApplication('fpBB', fpBB.blackbox)
      }, 5000)
    },

    setWindowHiddenFields () {
      const hiddenFields = {
        'aid': this.$store.state.application.aid,
        'ad_source': this.$store.state.application.ad_source,
        'sid': this.$store.state.application.SID,
        'lead_source': this.$store.state.application.lead_source
      }
      window.cd_hidden_fields = hiddenFields
    }
  },

  // Render
  render: () => {
    return undefined
  }
}
</script>
